import { op } from "libs/transaction";
import { createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import { getPointer, isGroupTagRecord, TagSubscriptionPreference } from "libs/schema";
import { authorizeUserCanSubscribeToTag } from "./authorizers/tagAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  updateTagSubscription
 * -------------------------------------------------------------------------------------------------
 */

type UpdateTagSubscriptionProps = {
  tagId: string;
  preference: TagSubscriptionPreference;
  ownerOrganizationId: string;
};

export const updateTagSubscription = createAction<UpdateTagSubscriptionProps>(
  "updateTagSubscription.updateTagSubscription",
  {
    operations: async (props, context) => {
      const { loader } = context.environment;

      const [tag] = await loader.getRecord("tag", props.tagId);

      if (!tag) {
        throw new InvalidActionStateError("[updateTagSubscription] Tag not found", { errorData: { props } });
      }

      if (isGroupTagRecord(tag) && tag.data?.is_organization_group) {
        throw new InvalidActionStateError("[updateTagSubscription] Cannot update organization group subscription");
      }

      const pointer = getPointer("tag_subscription", {
        tag_id: props.tagId,
        user_id: context.currentUserId,
      });

      return op.set("tag_subscription", {
        id: pointer.id,
        creator_user_id: context.currentUserId,
        owner_organization_id: props.ownerOrganizationId,
        preference: props.preference,
        tag_id: props.tagId,
        tag_type: tag.type,
        user_id: context.currentUserId,
      });
    },
    authorize: async (props, context) => {
      await authorizeUserCanSubscribeToTag({ tagId: props.tagId, context });
    },
  },
);

/* -------------------------------------------------------------------------------------------------
 *  deleteTagSubscription
 * -------------------------------------------------------------------------------------------------
 */

type DeleteTagSubscriptionProps = {
  tagId: string;
};

export const deleteTagSubscription = createAction<DeleteTagSubscriptionProps>(
  "updateTagSubscription.deleteTagSubscription",
  {
    operations: async (props, context) => {
      const {
        currentUserId,
        environment: { loader },
      } = context;

      const [tag] = await loader.getRecord("tag", props.tagId);

      if (!tag) {
        throw new InvalidActionStateError("[deleteTagSubscription] Tag not found", { errorData: { props } });
      }

      // The current implementation for `@mentioning` organizations expects all members of an organization to have
      // a `tag_subscription` record to that organization.
      //
      // TODO @jorroll: update `@mentioning` organizations to not rely on having `tag_subscription` records
      if (isGroupTagRecord(tag) && tag.data?.is_organization_group) {
        throw new InvalidActionStateError("[deleteTagSubscription] Cannot delete organization group subscription");
      }

      return op.delete(
        getPointer("tag_subscription", {
          tag_id: props.tagId,
          user_id: currentUserId,
        }),
      );
    },
    authorize: async () => {
      // Users are permitted to delete their own tag_subscription records, and the current implementation
      // with `context.currentUserId` ensures that only their records can be deleted, so no authorization is necessary.
    },
  },
);

export const actions = {
  updateTagSubscription,
  deleteTagSubscription,
};
